import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LEDGER_PARTY_ROLES, type Ledger, type LedgerPartyRole, type Vendor } from '@fpc/shared';
import { api } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { humanize } from '@/lib/format';
import { describeError } from '@/lib/errors';
import { ErrorState, Modal, Spinner, ToneBadge } from './ui';
import { ExpenseLedgerOptions, useExpenseLedgers } from './ExpenseLedgerOptions';

/** Suggested code and name for a new account of this role, e.g. DAIKIN-ADV, "Daikin – Advance". */
const SUFFIX: Record<LedgerPartyRole, string> = {
  PAYABLE: 'PAY',
  ADVANCE: 'ADV',
  DEPOSIT: 'DEP',
  OTHER: 'OTH',
};

export const suggestedVendorAccount = (
  vendor: Pick<Vendor, 'code' | 'name'>,
  role: LedgerPartyRole,
) => ({
  code: `${vendor.code}-${SUFFIX[role]}`.toUpperCase(),
  name: `${vendor.name} – ${humanize(role)}`,
});

/**
 * A vendor's own accounts in the company's ledgers: its Payable (created with
 * the vendor), and any Advance, Deposit or extra Payable accounts. Accounting
 * picks one of these when it verifies the vendor's invoice.
 *
 * Also the vendor's default expense ledger — what its invoices are usually
 * for — which pre-fills them for accounting to confirm.
 */
export function VendorLedgersPanel({ vendor, onClose }: { vendor: Vendor; onClose(): void }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const canAdd = can('ledger:create');
  const canEdit = can('ledger:update');

  const ledgers = useQuery({
    queryKey: ['vendor-ledgers', vendor.id],
    queryFn: () =>
      api.settings.ledgers({ companyId: vendor.companyId, vendorId: vendor.id, pageSize: 100 }),
  });
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['vendor-ledgers', vendor.id] });
    void queryClient.invalidateQueries({ queryKey: ['settings-ledgers'] });
  };

  const [role, setRole] = useState<LedgerPartyRole>('ADVANCE');
  const [draft, setDraft] = useState(suggestedVendorAccount(vendor, 'ADVANCE'));
  const pickRole = (next: LedgerPartyRole) => {
    setRole(next);
    setDraft(suggestedVendorAccount(vendor, next));
  };

  const add = useMutation({
    mutationFn: () =>
      api.settings.createLedger({
        companyId: vendor.companyId,
        vendorId: vendor.id,
        partyRole: role,
        code: draft.code,
        name: draft.name,
      }),
    onSuccess: refresh,
  });
  const makeDefault = useMutation({
    mutationFn: (ledger: Ledger) => api.settings.updateLedger(ledger.id, { isDefault: true }),
    onSuccess: refresh,
  });

  const rows = (ledgers.data?.items ?? []).filter((ledger) => ledger.active);

  const canSetDefault = can('vendor:update');
  const { ledgers: expenseLedgers } = useExpenseLedgers(vendor.companyId);
  const [defaultExpense, setDefaultExpense] = useState(vendor.defaultExpenseLedgerId ?? '');
  const saveDefault = useMutation({
    mutationFn: (ledgerId: string) =>
      api.settings.updateVendor(vendor.id, { defaultExpenseLedgerId: ledgerId }),
    onSuccess: (_saved, ledgerId) => {
      setDefaultExpense(ledgerId);
      void queryClient.invalidateQueries({ queryKey: ['settings-vendors'] });
      void queryClient.invalidateQueries({ queryKey: ['vendor', vendor.id] });
    },
  });

  return (
    <Modal title={`Ledger accounts — ${vendor.name}`} onClose={onClose} wide>
      <p className="mb-3 text-sm text-ink-500">
        The vendor’s own accounts in this company’s ledgers. Accounting picks one when it verifies
        the vendor’s invoice; the default is chosen unless another is picked.
      </p>

      <div className="mb-4">
        <label className="block">
          <span className="label">Default expense ledger</span>
          <select
            className="input"
            value={saveDefault.isPending ? (saveDefault.variables ?? '') : defaultExpense}
            disabled={!canSetDefault || saveDefault.isPending}
            onChange={(event) => saveDefault.mutate(event.target.value)}
          >
            <option value="">None</option>
            <ExpenseLedgerOptions ledgers={expenseLedgers} />
          </select>
        </label>
        <span className="mt-1 block text-xs text-ink-500">
          What this vendor’s invoices are usually for. It pre-fills each invoice; accounting
          confirms or changes it at verification.
        </span>
        {saveDefault.error ? (
          <span className="mt-1 block text-sm text-red-700">
            {describeError(saveDefault.error)}
          </span>
        ) : null}
      </div>

      {ledgers.isLoading ? (
        <Spinner />
      ) : ledgers.error ? (
        <ErrorState error={ledgers.error} />
      ) : !rows.length ? (
        <p className="py-6 text-center text-sm text-ink-500">
          No accounts yet. One Payable account is created the first time an invoice of this vendor
          is verified.
        </p>
      ) : (
        <ul className="divide-y divide-ink-100 rounded-lg border border-ink-100">
          {rows.map((ledger) => (
            <li key={ledger.id} className="flex flex-wrap items-center gap-3 px-3 py-2.5">
              <span className="font-mono text-xs text-ink-500">{ledger.code}</span>
              <span className="min-w-0 flex-1 truncate text-sm font-medium text-ink-900">
                {ledger.name}
              </span>
              <ToneBadge tone="neutral">{humanize(ledger.partyRole ?? 'OTHER')}</ToneBadge>
              {ledger.isDefault ? (
                <ToneBadge tone="positive">Default</ToneBadge>
              ) : canEdit ? (
                <button
                  type="button"
                  className="text-xs font-medium text-brand-700 hover:underline"
                  disabled={makeDefault.isPending}
                  onClick={() => makeDefault.mutate(ledger)}
                >
                  Make default
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {canAdd ? (
        <form
          className="mt-4 grid gap-3 rounded-lg bg-ink-50 p-3 sm:grid-cols-[9rem_10rem_1fr_auto] sm:items-end"
          onSubmit={(event) => {
            event.preventDefault();
            add.mutate();
          }}
        >
          <label className="block">
            <span className="label">Account for</span>
            <select
              className="input"
              value={role}
              onChange={(event) => pickRole(event.target.value as LedgerPartyRole)}
            >
              {LEDGER_PARTY_ROLES.map((option) => (
                <option key={option} value={option}>
                  {humanize(option)}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="label">Code</span>
            <input
              className="input font-mono"
              value={draft.code}
              onChange={(event) => setDraft({ ...draft, code: event.target.value })}
            />
          </label>
          <label className="block">
            <span className="label">Name</span>
            <input
              className="input"
              value={draft.name}
              onChange={(event) => setDraft({ ...draft, name: event.target.value })}
            />
          </label>
          <button type="submit" className="btn-primary" disabled={add.isPending}>
            Add account
          </button>
          {add.error ? (
            <p className="text-sm text-red-700 sm:col-span-4">{describeError(add.error)}</p>
          ) : null}
        </form>
      ) : null}
    </Modal>
  );
}
